import { ComponentLoader, Field } from '@snsw/react-component-library';
import { useBoolean } from 'react-use';
import { FileUploadInput } from './FileUploadInput';
import { useDetectHasDragDrop } from 'components/Utils/useDetectHasDragDrop';
import { ForwardedRef, forwardRef, ReactNode, useState } from 'react';
import { FileUploadApi } from './FileUpload.api';
import { FormFieldset, FormLegend, FormLegendLabel, SROnly } from './Form.styled';
import { UploadedFileItem } from './UploadFileItem';

export interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
}

export interface FileUploadProps {
  id: string;
  name: string;
  api: FileUploadApi;
  initialFiles?: UploadedFile[];
  legendText?: string | ReactNode;
  introText?: string | ReactNode;
  maxMBSize?: number;
  maxTotalMBSize?: number;
  maxFiles?: number;

  /**
   * Case-insensitive comma separated list of file extensions that should be supported.
   */
  acceptedFormats?: string;

  /**
   * Optional custom text to display for the accepted formats. If omitted, the `acceptedFormats` file extension list
   * will be shown instead.
   */
  acceptedFormatsText?: string | null;

  /**
   * Force the control to disable drag-and-drop mode, even if the user's device is capable of it.
   */
  disableDragDrop?: boolean;

  /**
   * Callback invoked after an upload has completed successfully.
   *
   * @param newFiles The cumulative list of all files that have been uploaded (ie. not just the ones that were
   * uploaded in the last operation).
   */
  onUploaded?: (newFiles: UploadedFile[]) => void;

  /**
   * Callback invoked after a file has been removed.
   * @param newFiles The list of files that remain after the file was removed.
   */
  onRemoved?: (newFiles: UploadedFile[]) => void;

  /**
   * Error message to be displayed. Internal error messages take precedence over this.
   */
  errorMessage?: string;
}

const getErrorMessage = (error?: Error | unknown) => (error as Error)?.message ?? 'The request was unsuccessful.';

const FileUpload = forwardRef((props: FileUploadProps, ref: ForwardedRef<HTMLFieldSetElement>) => {
  const {
    id,
    name,
    initialFiles,
    legendText,
    introText,
    maxMBSize,
    maxTotalMBSize,
    maxFiles,
    acceptedFormats,
    acceptedFormatsText,
    disableDragDrop,
    onUploaded,
    onRemoved,
    errorMessage,
    api,
  } = props;

  const hasDragDrop = useDetectHasDragDrop();
  const allowDragDrop = !!(hasDragDrop && !disableDragDrop);

  const [internalError, setInternalError] = useState('');
  const [loading, setLoading] = useBoolean(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(initialFiles ?? []);
  const bytesInMegabyte = 1024 * 1024;
  const maxSize = maxMBSize * bytesInMegabyte;
  const maxCombinedSize = maxTotalMBSize * bytesInMegabyte;
  const acceptedFormatsRegex = new RegExp('^(' + acceptedFormats.replace(/,/g, '|').replace(/\./g, '\\.') + ')$', 'i');

  const hasMaxFiles = maxFiles > 0 && uploadedFiles.length >= maxFiles;

  const removeFile = async (fileId) => {
    setLoading(true);

    try {
      await api.removeFile(fileId);

      const newFiles = uploadedFiles.filter((file) => file.id !== fileId);
      setUploadedFiles(newFiles);
      onRemoved && onRemoved(newFiles);
    } catch (error: Error | unknown) {
      setInternalError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const isFileSmallerThanMax = (file: File) => !maxMBSize || file.size <= maxSize;

  const hasValidExtension = (file: File) => {
    if (!acceptedFormats) return true;

    // See: https://stackoverflow.com/a/12900504/127497
    const fname = file.name;
    const extension = fname.slice(((fname.lastIndexOf('.') - 1) >>> 0) + 1);

    return acceptedFormatsRegex.test(extension);
  };

  const hasExceededMaxFiles = (files) => files.length + uploadedFiles.length > maxFiles;

  const hasExceededMaxCombinedSize = (files) => {
    if (!maxTotalMBSize) return false;

    const totalFileSize = [...uploadedFiles, ...files].reduce((previous, current) => previous + current.size, 0);
    return totalFileSize > maxCombinedSize;
  };

  const checkFilesBeforeUpload = (file) => {
    const validSize = isFileSmallerThanMax(file);
    const validExtension = hasValidExtension(file); // TODO: should be using reported mime type instead looking at extensions

    if (!validSize) {
      setInternalError(`The file you have attempted to upload is too big. Please try again with a smaller file.`);
      return false;
    }

    if (!validExtension) {
      setInternalError('Unsupported file extension.');
      return false;
    }

    return file;
  };

  const getFileCountDescription = () => (uploadedFiles?.length > 1 ? 'one or more of the files' : 'the uploaded file');

  const handleFiles = async (files: FileList) => {
    if (hasExceededMaxFiles(files)) {
      setInternalError(`Too many files have been selected, please remove ${getFileCountDescription()} and try again.`);
      return;
    }

    if (hasExceededMaxCombinedSize(files)) {
      setInternalError(
        `Combined file size limit has been exceed, please remove ${getFileCountDescription()} and try again.`
      );

      return;
    }

    setInternalError('');
    setLoading();

    const newFiles = [...uploadedFiles];

    // loop files from the input (cannot use forEach, must loop without a callback)
    try {
      for (const file of Array.from(files)) {
        // validate the file
        const validatedFile = checkFilesBeforeUpload(file);
        if (validatedFile) {
          // if valid, attempt to upload the file
          // NB: we must await this promise before pushing to array, as we're in a for..of loop
          const fileId = await api.uploadFile(validatedFile);
          if (fileId) {
            newFiles.push({ id: fileId, name: file.name, size: file.size, type: file.type });
          }
        }
      }

      // set the state with newly uploaded file(s)
      setUploadedFiles(newFiles);
      onUploaded && onUploaded(newFiles);
    } catch (error: Error | unknown) {
      setInternalError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const allowMultiple = maxFiles > 1;

  const hasAcceptedFormatsHelpText = !!acceptedFormats && acceptedFormatsText !== null;
  const sizeAndFormatHelpText = (
    <>
      {hasAcceptedFormatsHelpText && (
        <>
          Formats accepted: {acceptedFormatsText ?? acceptedFormats}.
          <br />
        </>
      )}
      {maxMBSize && (
        <>
          {allowMultiple ? 'Individual file' : 'File'} size must not exceed {maxMBSize} MB.
          <br />
        </>
      )}
      {allowMultiple && maxTotalMBSize ? `Combined file size must not exceed ${maxTotalMBSize} MB.` : ''}
    </>
  );

  const errorToDisplay = internalError || errorMessage;
  const hasError = !!errorToDisplay;

  const margins = {
    margin: '1rem 0',
  };

  return (
    <>
      <FormFieldset style={margins} aria-invalid={hasError} ref={ref}>
        <FormLegend>
          <FormLegendLabel>{legendText}</FormLegendLabel>
          <SROnly>
            {sizeAndFormatHelpText} {introText}
          </SROnly>
          {hasError && <SROnly>{errorToDisplay}</SROnly>}
        </FormLegend>
        <div style={margins} aria-hidden={true}>
          {introText}
        </div>
        <ComponentLoader active={loading} label="Please wait..." />
        {hasMaxFiles && <p>To upload a different file, please remove {getFileCountDescription()} below.</p>}

        {!hasMaxFiles && (
          <FileUploadInput
            id={id}
            name={name}
            helpMessage={
              <p>
                {allowDragDrop && (
                  <>Drag and drop files into this box or use the button to select and upload your files.</>
                )}
                {!allowDragDrop && <>Use the button to select and upload your files.</>}
                <br />
                {sizeAndFormatHelpText}
              </p>
            }
            disableDragDrop={!allowDragDrop}
            hasError={hasError}
            onChange={handleFiles}
            allowMultiple={allowMultiple}
            acceptedFormats={acceptedFormats}
          />
        )}

        {hasError && <Field.Error aria-hidden>{errorToDisplay}</Field.Error>}
      </FormFieldset>
      {uploadedFiles &&
        uploadedFiles.map((uploadedFile) => (
          <UploadedFileItem key={uploadedFile.id} {...uploadedFile} onRemove={removeFile} disabled={loading} />
        ))}
    </>
  );
});

FileUpload.defaultProps = {
  legendText: 'Upload file',
  maxFiles: 1,
  acceptedFormats: '',
  initialFiles: [],
};

export default FileUpload;
